/**
 * @file Fullscreen preview of one attachment version. Consumers: `Attachments`,
 * `EmailExpandedContent` and `ApiExpandedContent`, each rendering it once
 * against a local target.
 *
 * The preview resolves bytes only for a kind it can actually render. A version
 * whose `previewKind` is `unknown` — a `.dwg` and anything else outside image,
 * PDF, Office and text, which is the population chunking exists for — renders
 * the fallback notice
 * without a single request: assembling 700 MB from 350 chunks to then say
 * "Preview not available" is the one outcome worth spending nothing on. The
 * Download control stays enabled either way, because it does its own read.
 *
 * A `text` version is decoded and rendered as characters, never framed. Its
 * bytes are untrusted — an inbound email keeps the sender's declared type
 * verbatim — and a `blob:` document inherits this page's origin, so a framed
 * `text/html` attachment would run its scripts as the signed-in user.
 *
 * `previewKind` narrows `resolveAttachmentFileType`, the application's one
 * file-type rule, so a file's icon and its preview can never disagree. That rule
 * reads a known extension before the declared content type: a version
 * mislabelled `text/plain` previews as the PDF its name says it is, and a `.dwg`
 * declared `application/pdf` reaches the fallback notice instead of an empty PDF
 * frame.
 *
 * The header's Extract schedule control sits beside Download and calls
 * `useExtractSchedule` itself, as Download calls `useAttachmentDownload`. Its
 * file type is the same `fileType` the preview kind narrows, and it starts only
 * on a `form` host; the overlay is never opened inside a PDF export, so it
 * carries no export gate.
 *
 * The `layout` switch is not here — `resolveAttachmentVersionSource` owns it for
 * this overlay and the download funnel alike.
 */
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Alert, Button, Modal, Spin, Typography, theme } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { doc, getDoc } from 'firebase/firestore';

import FlexBox from '@/components/atoms/FlexBox';
import FlexCol from '@/components/atoms/FlexCol';
import { MessageContext } from '@/providers/MessageContext';
import { firestore } from '@/services/startFirestore';
import {
  resolveAttachmentVersionSource,
  type AttachmentVersionSource,
} from '@/storage/resolveAttachmentVersionSource';
import { useAttachmentDownload } from '@/use/attachments/useAttachmentDownload';
import type { Attachment } from '#types/System.types';
import OfficePreviewPane from './OfficePreviewPane';
import { resolveAttachmentFileType } from './resolveAttachmentFileType';
import type { AttachmentFileType, ExtractScheduleHost } from './index.types';
import { ExtractScheduleButton } from './ExtractScheduleButton';
import { resolveExtractScheduleBlockedReason } from './resolveExtractScheduleBlockedReason';
import { useExtractSchedule } from '@/use/attachments/useExtractSchedule';

const { Text } = Typography;

type PreviewKind = 'image' | 'pdf' | 'office' | 'text' | 'unknown';

const PREVIEW_KIND_BY_FILE_TYPE: Record<AttachmentFileType, PreviewKind> = {
  pdf: 'pdf',
  image: 'image',
  word: 'office',
  spreadsheet: 'office',
  csv: 'text',
  text: 'text',
  email: 'unknown',
  cad: 'unknown',
  bim: 'unknown',
  unknown: 'unknown',
};

interface AttachmentPreviewOverlayProps {
  open: boolean;
  onClose: () => void;
  fileUid?: string;
  versionId?: string;
  fileName?: string;
  declaredContentType?: string;
  /** Where the overlay is hosted, which decides whether Extract schedule can write to a form. */
  extractScheduleHost: ExtractScheduleHost;
}

/**
 * Fullscreen attachment preview overlay.
 *
 * Minimal integration pattern:
 * - Keep local state: `{ fileUid, versionId, fileName, declaredContentType? } | null`
 * - Set that state from any UI control (button/menu) to open the overlay
 * - Render this component once and pass `open={Boolean(state)}` and `onClose={() => setState(null)}`
 */
const AttachmentPreviewOverlay: FC<AttachmentPreviewOverlayProps> = ({
  open,
  onClose,
  fileUid,
  versionId,
  fileName,
  declaredContentType,
  extractScheduleHost,
}) => {
  const { token } = theme.useToken();
  const { messageApi } = useContext(MessageContext);
  const { downloadAttachment } = useAttachmentDownload();
  const { extractSchedule, extractingVersionId, canExtract } =
    useExtractSchedule(messageApi);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [resolvedContentType, setResolvedContentType] = useState<string | null>(
    null,
  );

  const objectUrlRef = useRef<string | null>(null);

  const normalizedFileName = useMemo(() => (fileName || '').trim(), [fileName]);
  const fileExtension = useMemo(() => {
    const parts = normalizedFileName.toLowerCase().split('.');
    return parts.length > 1 ? parts[parts.length - 1] : '';
  }, [normalizedFileName]);

  const fileType = useMemo(
    () => resolveAttachmentFileType(normalizedFileName, declaredContentType),
    [declaredContentType, normalizedFileName],
  );
  const previewKind = PREVIEW_KIND_BY_FILE_TYPE[fileType];

  const revokeObjectUrl = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  /**
   * Reads the version record this overlay was opened against, then hands it to
   * the shared resolver. The `type` it carries seeds the header's content type,
   * which is why this read happens here rather than inside the resolver.
   */
  const readVersionSource =
    useCallback(async (): Promise<AttachmentVersionSource> => {
      if (!fileUid || !versionId) {
        throw new Error('Missing file reference.');
      }

      const attachmentRef = doc(firestore, 'attachments', fileUid);
      const attachmentSnap = await getDoc(attachmentRef);
      if (!attachmentSnap.exists()) {
        throw new Error('Attachment not found.');
      }

      const attachmentData = attachmentSnap.data() as Partial<Attachment>;
      const version = attachmentData.versions?.[versionId];

      const versionType = version?.type;
      if (versionType) {
        setResolvedContentType((prev) => prev || versionType);
      }

      return resolveAttachmentVersionSource({
        fileUid,
        versionId,
        version,
        attachment: attachmentData,
        fileName: fileName || 'file',
      });
    }, [fileName, fileUid, versionId]);

  /**
   * Resolves the bytes this overlay can render, and nothing else. An unknown
   * kind returns before any request: the fallback notice is the whole outcome,
   * and a chunked version would otherwise pay for a full assembly to reach it.
   *
   * An assembled version is already in hand and already verified, so it needs no
   * second round trip. One object URL serves both the download button and the
   * preview frame, and `objectUrlRef` owns revoking it either way.
   *
   * A `text` version is decoded to characters instead: it is rendered as text,
   * so it never needs a URL a browsing context could load.
   */
  const loadPreview = useCallback(async () => {
    if (!open) return;
    if (!fileUid || !versionId) return;

    setIsLoading(true);
    setError(null);
    setDownloadUrl(null);
    setObjectUrl(null);
    setTextContent(null);
    revokeObjectUrl();

    if (previewKind === 'unknown') {
      setIsLoading(false);
      return;
    }

    try {
      const source = await readVersionSource();

      if (source.kind === 'blob') {
        const assembledUrl = URL.createObjectURL(source.blob);
        objectUrlRef.current = assembledUrl;
        setDownloadUrl(assembledUrl);
        if (previewKind === 'text') setTextContent(await source.blob.text());
        else if (previewKind !== 'office') setObjectUrl(assembledUrl);
        setIsLoading(false);
        return;
      }

      const url = source.url;
      setDownloadUrl(url);

      if (previewKind === 'office') {
        setIsLoading(false);
        return;
      }

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to load file (${response.status})`);
      }
      const blob = await response.blob();
      const responseContentType = response.headers.get('content-type');
      if (responseContentType) {
        setResolvedContentType(responseContentType);
      }

      if (previewKind === 'text') {
        setTextContent(await blob.text());
        return;
      }

      const nextObjectUrl = URL.createObjectURL(blob);
      objectUrlRef.current = nextObjectUrl;
      setObjectUrl(nextObjectUrl);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load preview.');
    } finally {
      setIsLoading(false);
    }
  }, [
    fileUid,
    open,
    previewKind,
    readVersionSource,
    revokeObjectUrl,
    versionId,
  ]);

  useEffect(() => {
    void loadPreview();
    return () => {
      revokeObjectUrl();
    };
  }, [loadPreview, revokeObjectUrl]);

  /**
   * `downloadAttachment` answers with `{ success: false, error }` instead of
   * throwing, and the reader writes that message to be shown as-is, so the
   * failure reaches the user verbatim rather than as a file that never arrives.
   * A cancel is the one unsuccessful result that raises nothing: the user asked
   * for it, so there is nothing to tell them.
   */
  const handleDownload = useCallback(async () => {
    if (!fileUid || !versionId || !normalizedFileName) return;
    const result = await downloadAttachment({
      fileUid,
      versionId,
      fileName: normalizedFileName,
    });
    if (!result.success && !result.cancelled) {
      messageApi.error(
        result.error?.message ?? `Failed to download "${normalizedFileName}"`,
      );
    }
  }, [downloadAttachment, fileUid, messageApi, normalizedFileName, versionId]);

  const handleExtractSchedule = useCallback(() => {
    if (
      !fileUid ||
      !versionId ||
      !normalizedFileName ||
      extractScheduleHost.kind !== 'form'
    )
      return;
    void extractSchedule({
      fileUid,
      versionId,
      fileName: normalizedFileName,
      order: extractScheduleHost.order,
    });
  }, [
    extractSchedule,
    extractScheduleHost,
    fileUid,
    normalizedFileName,
    versionId,
  ]);

  const headerTitle = useMemo(() => {
    if (!normalizedFileName) return 'Attachment Preview';
    return normalizedFileName;
  }, [normalizedFileName]);

  const showFallback = previewKind === 'unknown';

  return (
    <Modal
      title={
        <FlexBox noGrow justifyStart gap={token.marginLG}>
          <Text>{headerTitle}</Text>
          <Text type="secondary" ellipsis style={{ maxWidth: 600 }}>
            {resolvedContentType ||
              declaredContentType ||
              (fileExtension ? `.${fileExtension}` : 'Unknown type')}
          </Text>
          <Button
            type="link"
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => void handleDownload()}
            disabled={!fileUid || !versionId || !normalizedFileName}
            aria-label="Download attachment"
          >
            Download
          </Button>
          <ExtractScheduleButton
            id="attachment-preview-extract-schedule-button"
            fileName={normalizedFileName}
            blockedReason={resolveExtractScheduleBlockedReason({
              fileType,
              host: extractScheduleHost,
              canExtract,
            })}
            busy={!fileUid || !versionId || !normalizedFileName}
            loading={extractingVersionId === versionId}
            onClick={handleExtractSchedule}
          />
        </FlexBox>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width="100vw"
      style={{
        top: token.marginLG,
        maxWidth: `calc(100vw - ${token.marginLG * 2}px)`,
        background: token.colorFillTertiary,
        borderRadius: token.borderRadiusLG,
      }}
      styles={{
        body: {
          height: `calc(100vh - ${token.marginLG * 2 + 100}px)`,
          padding: 0,
          overflowY: 'auto',
        },
      }}
      destroyOnHidden
      aria-label="Attachment preview overlay"
    >
      <FlexCol stretch gap={0} style={{ height: '100%' }}>
        <FlexCol stretch style={{ height: '100%' }}>
          {error && (
            <Alert
              type="error"
              showIcon
              title="Preview failed"
              description={error}
              style={{ margin: 10 }}
            />
          )}

          {isLoading && (
            <FlexBox justifyCenter stretch style={{ padding: 40 }}>
              <Spin size="large" />
            </FlexBox>
          )}

          {!isLoading && !error && previewKind === 'image' && objectUrl && (
            <FlexBox
              justifyCenter
              stretch
              style={{ height: '100%', padding: token.paddingSM }}
            >
              <img
                src={objectUrl}
                alt={normalizedFileName || 'Attachment image preview'}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  borderRadius: token.borderRadiusLG,
                }}
              />
            </FlexBox>
          )}

          {!isLoading && !error && previewKind === 'pdf' && objectUrl && (
            <iframe
              src={`${objectUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
              title="PDF Attachment Preview"
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          )}

          {!isLoading &&
            !error &&
            previewKind === 'text' &&
            textContent !== null && (
              <FlexCol
                stretch
                aria-label="Text attachment preview"
                style={{
                  height: '100%',
                  overflow: 'auto',
                  padding: token.padding,
                  background: token.colorBgContainer,
                  borderRadius: token.borderRadiusLG,
                }}
              >
                <Text
                  code
                  style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  {textContent}
                </Text>
              </FlexCol>
            )}

          {!isLoading && !error && previewKind === 'office' && downloadUrl && (
            <OfficePreviewPane
              downloadUrl={downloadUrl}
              fileName={normalizedFileName}
            />
          )}

          {!isLoading && !error && showFallback && (
            <FlexCol stretch style={{ padding: 20 }}>
              <Alert
                type="info"
                showIcon
                title="Preview not available"
                description="This file type can be downloaded, but it can’t be previewed in-app yet."
              />
            </FlexCol>
          )}
        </FlexCol>
      </FlexCol>
    </Modal>
  );
};

export default AttachmentPreviewOverlay;
